import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import callService from '../services/CallService';

export default function MissedCallNotification({ serviceId, contactId }) {
  const [missedCalls, setMissedCalls] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const previousCallIdsRef = useRef(new Set());

  useEffect(() => {
    if (!serviceId) return;
    
    const fetchUnreadCount = async () => {
      try {
        const calls = await callService.getUnreadMissedCalls(serviceId, contactId, 20) || [];
        
        setUnreadCount(calls.length);  // Only unread count
        
      } catch (error) {
        console.error('Fetch unread missed calls error:', error);
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 5000);
    
    return () => clearInterval(interval);
  }, [serviceId, contactId]);

  // FETCH ALL missed calls when opening popup
 
  const fetchAllMissedCalls = async () => {
    try {
      const calls = await callService.getMissedCalls(serviceId, contactId, 50) || [];
      setMissedCalls(calls);
    } catch (error) {
      console.error('Fetch all missed calls error:', error);
    }
  };

  const handleOpenList = async (event) => {
    setAnchorEl(event.currentTarget);
    
    // Fetch all calls to display in popup
    await fetchAllMissedCalls();
    try {
      await callService.markMissedCallsAsRead(serviceId, contactId);
      setUnreadCount(0);  // Immediately update badge
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleCloseList = () => {
    setAnchorEl(null);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Bell Icon with Badge */}
      <IconButton 
        color="inherit" 
        onClick={handleOpenList}
        sx={{ position: 'relative', ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <PhoneMissedIcon />
        </Badge>
      </IconButton>

      {/* Missed Calls List Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseList}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, maxHeight: 400, borderRadius: 2 } }}
      >
        <Box sx={{ p: 2, bgcolor: '#fff3e0', borderBottom: '1px solid #ffe0b2' }}>
          <Typography variant="subtitle1" fontWeight="bold" color="#e65100">
            Missed Calls ({missedCalls.length})
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          {missedCalls.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <PhoneMissedIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No missed calls</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {missedCalls.map((call, index) => (
                <React.Fragment key={call.call_id}>
                  <ListItem sx={{ bgcolor: '#fff8e1' }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#ff9800' }}>
                        <PhoneMissedIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={call.contact_name || call.contact_phone || 'Unknown'}
                      secondary={`${formatTime(call.created_at)} • Missed`}
                    />
                  </ListItem>
                  {index < missedCalls.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}